// ── localStorage that cannot take the app down ───────────────────────────────
// Touching window.localStorage THROWS (SecurityError) when site data is
// blocked — Chrome/Brave with "block all cookies", a page embedded in an
// iframe with third-party storage blocked — and setItem throws
// QuotaExceededError when the store is full. It is not enough to guard the
// value: the property access itself is what throws.
//
// This mattered because CookieBanner reads localStorage in a mount effect and
// is rendered site-wide. An error thrown in useEffect propagates to the nearest
// error boundary, so those users saw "Something went wrong" on EVERY page
// rather than simply missing a cookie banner.
//
// Same shape as the real API so call sites read unchanged, except that nothing
// here throws: reads fall back, writes report success as a boolean.

let available = null; // null = not probed yet

// Probe once, by ACCESS only — never by writing. A test write would report the
// store unusable whenever the quota is full, and reads still work fine in that
// state (as they do in old iOS private mode, where getItem works and every
// setItem throws). Write failures are handled by setItem's own catch instead,
// so a full store degrades to "reads fine, writes refused" rather than losing
// the user's saved theme and dismissals outright.
const store = () => {
  if (available === false) return null;
  try {
    const s = window.localStorage;
    if (!s) { available = false; return null; }
    available = true;
    return s;
  } catch {
    available = false;
    return null;
  }
};

export const getItem = (key, fallback = null) => {
  try {
    const s = store();
    if (!s) return fallback;
    const v = s.getItem(key);
    return v === null ? fallback : v;
  } catch {
    return fallback;
  }
};

export const setItem = (key, value) => {
  try {
    const s = store();
    if (!s) return false;
    s.setItem(key, String(value));
    return true;
  } catch {
    // Quota exceeded, or storage revoked mid-session.
    return false;
  }
};

export const removeItem = (key) => {
  try {
    const s = store();
    if (!s) return false;
    s.removeItem(key);
    return true;
  } catch {
    return false;
  }
};

// Convenience for the sites that store JSON — a corrupt or truncated value
// should behave exactly like a missing one, never throw at the call site.
export const getJSON = (key, fallback = null) => {
  const raw = getItem(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

export const setJSON = (key, value) => {
  try {
    return setItem(key, JSON.stringify(value));
  } catch {
    return false;
  }
};

// True when storage actually works — for deciding whether to offer a
// "remember this" affordance at all.
export const storageAvailable = () => store() !== null;

const safeStorage = { getItem, setItem, removeItem, getJSON, setJSON, storageAvailable };
export default safeStorage;
